import {
    builderObject,
    ownedUnifiedGroupsLeftNavId,
    PersonType,
    joinedUnifiedGroupsLeftNavId,
    deletedUnifiedGroupsLeftNavId,
    groupMemberExpandedViewSection,
    groupDefaultExpandedViewSection,
    groupHubUrlKey,
    groupHubDeletedFolderUrlKey,
    groupHubMemberFolderUrlKey,
    groupHubOwnerFolderUrlKey,
    groupMemberTabUrlKey,
} from '../../../../shared';
import type { NavigationState, ClickedHubPersonaState } from '../../../state/view/navigationState';
import {
    unifiedGroupClickedAction,
    leaveGroupCompletedAction,
    unifiedGroupListFilterChangedAction,
    unifiedGroupListScrollToGroupClickedAction,
    notificationBarViewApprovedGroupClickedAction,
    restoreGroupCompletedAction,
    notificationBarViewRestoredGroupClickedAction,
    PersonaClickedOrigin,
    UnifiedGroupClickedOrigin,
    loadGroupForDeepLinkAction,
    loadGroupFolderForDeepLinkAction,
    handleSoftDeletedGroupCompletedAction,
    unifiedGroupTransferOwnershipAction,
} from '../../../../hub';
import {
    dismissCreateGroupDialogAction,
    deleteGroupCompletedAction,
} from '../../../../editors/actions';
import {
    lpcDemoteSelfCompletedAction,
    lpcLeaveGroupCompletedAction,
    LeaveGroupActionPayload,
    lpcUpdateTransferOwnershipAction,
} from '../../..';
import { deepLinkGroupUrlClickedAction } from '../../../../hub/actions/data/groups/unifiedGroupActions';

const unifiedGroupList = 'unifiedGroupList';

export default (reducer: builderObject<NavigationState>): builderObject<NavigationState> =>
    reducer
        .handle(deleteGroupCompletedAction, (state, action) => {
            return updateState(
                state,
                action.payload.nextGroupInList,
                state.clickedHubPersona ? state.clickedHubPersona.origin : 'personaList'
            );
        })
        .handle(handleSoftDeletedGroupCompletedAction, (state, action) => {
            if (action.payload.hubPersonaId === state.clickedHubPersona?.hubPersonaId) {
                return {
                    ...state,
                    clickedHubPersona: {
                        hubPersonaId: action.payload.nextListItemId,
                        origin: state.clickedHubPersona
                            ? state.clickedHubPersona.origin
                            : 'personaList',
                    },
                };
            }
            return state;
        })
        .handle(dismissCreateGroupDialogAction, (state, action) => {
            const group = action.payload.group;
            if (group) {
                return {
                    ...state,
                    clickedHubPersona: {
                        hubPersonaId: group.hubPersonaId,
                        origin: unifiedGroupList,
                    },
                    clickedLeftNavItemId: ownedUnifiedGroupsLeftNavId,
                };
            }

            return state;
        })
        .handle(loadGroupForDeepLinkAction, (state, action) => {
            const clickedHubPersona: ClickedHubPersonaState = {
                hubPersonaId: action.payload.hubPersonaId,
                origin: 'DeepLink',
            };

            return {
                ...state,
                clickedLeftNavItemId: action.payload.leftNavItemId,
                clickedHubPersona,
                lastClickedHubPersonaBeforeEVNavigation: clickedHubPersona,
                deepLinkedGroupSmtp: undefined,
                currentRouteList: undefined,
            };
        })
        .handle(loadGroupFolderForDeepLinkAction, (state, action) => {
            const groupFolder = action.payload.groupFolder;
            var clickedLeftNavItemId = ownedUnifiedGroupsLeftNavId;
            var currentRouteList = [groupHubUrlKey];
            if (groupFolder === groupHubDeletedFolderUrlKey) {
                clickedLeftNavItemId = deletedUnifiedGroupsLeftNavId;
                currentRouteList.push(groupHubDeletedFolderUrlKey);
            } else if (groupFolder === groupHubMemberFolderUrlKey) {
                clickedLeftNavItemId = joinedUnifiedGroupsLeftNavId;
                currentRouteList.push(groupHubMemberFolderUrlKey);
            } else {
                currentRouteList.push(groupHubOwnerFolderUrlKey);
            }

            return {
                ...state,
                clickedLeftNavItemId,
                currentRouteList,
                clickedRightViewSection: undefined,
            };
        })
        .handle(deepLinkGroupUrlClickedAction, (state, action) => {
            const groupSmtp = action.payload.groupSmtp;
            var groupSmtpParts = groupSmtp.split('@');
            const currentRouteList =
                groupSmtpParts.length == 2
                    ? [groupHubUrlKey, groupSmtpParts[1], groupSmtpParts[0]]
                    : [groupHubUrlKey];
            const clickedRightViewSection =
                action.payload.tabName === groupMemberTabUrlKey
                    ? groupMemberExpandedViewSection
                    : groupDefaultExpandedViewSection;
            return {
                ...state,
                deepLinkedGroupSmtp: groupSmtp,
                currentRouteList,
                clickedLeftNavItemId: joinedUnifiedGroupsLeftNavId,
                clickedRightViewSection,
            };
        })
        .handleDataAction(leaveGroupCompletedAction, (state, action) => {
            return updateStateOnLeaveGroup(state, action.payload);
        })
        .handle(lpcLeaveGroupCompletedAction, (state, action) => {
            return updateStateOnLeaveGroup(state, action.payload);
        })
        .handle(lpcDemoteSelfCompletedAction, (state, action) => {
            return updateState(
                state,
                action.payload.nextListItemId,
                state.clickedHubPersona ? state.clickedHubPersona.origin : 'personaList'
            );
        })
        .handle(notificationBarViewApprovedGroupClickedAction, (state, action) => {
            return updateState(state, action.payload.hubPersonaId, unifiedGroupList);
        })
        .handle(notificationBarViewRestoredGroupClickedAction, (state, action) => {
            return {
                ...state,
                clickedHubPersona: {
                    hubPersonaId: action.payload.hubPersonaId,
                    hubPersonaType: PersonType.Group,
                    origin: unifiedGroupList,
                },
                clickedLeftNavItemId: ownedUnifiedGroupsLeftNavId,
            };
        })
        .handleDataAction(restoreGroupCompletedAction, (state, action) => {
            return updateState(state, action.payload.nextListItemId, unifiedGroupList);
        })
        .handle(unifiedGroupClickedAction, (state, action) => {
            const clickedHubPersona = {
                hubPersonaId: action.payload.unifiedGroupId,
                hubPersonaType: PersonType.Group,
                origin: action.payload.origin,
            };

            return {
                ...state,
                clickedHubPersona,
                lastClickedHubPersonaBeforeEVNavigation: clickedHubPersona,
                clickedRightViewSection: undefined,
                isTransferOwnershipDialog: false,
            };
        })
        .handle(unifiedGroupListFilterChangedAction, state => {
            return { ...state, clickedHubPersona: undefined };
        })
        .handle(unifiedGroupListScrollToGroupClickedAction, (state, action) => {
            return updateState(state, action.payload.firstHubGroupIdInGroup, unifiedGroupList);
        })
        .handle(unifiedGroupTransferOwnershipAction, (state, action) => {
            const clickedHubPersona = {
                hubPersonaId: action.payload.unifiedGroupId,
                hubPersonaType: PersonType.Group,
                origin: action.payload.origin,
            };

            return {
                ...state,
                clickedHubPersona,
                lastClickedHubPersonaBeforeEVNavigation: clickedHubPersona,
                clickedRightViewSection: undefined,
                isTransferOwnershipDialog: action.payload.isTransferOwnershipDialog,
            };
        })
        .handle(lpcUpdateTransferOwnershipAction, (state, action) => {
            return {
                ...state,
                isTransferOwnershipDialog: action.payload.isTransferOwnershipDialog,
            };
        });

function updateState(
    state: NavigationState,
    actionHubPersonaId: string,
    actionOrigin: PersonaClickedOrigin | 'searchSuggestion' | UnifiedGroupClickedOrigin | 'DeepLink'
): NavigationState {
    if (
        !actionHubPersonaId &&
        ((!state.clickedLeftNavItemId && actionOrigin === 'DeepLink') || !!state.currentQuery)
    ) {
        return {
            ...state,
            clickedHubPersona: undefined,
            clickedLeftNavItemId: ownedUnifiedGroupsLeftNavId,
        };
    }
    return {
        ...state,
        clickedHubPersona: {
            hubPersonaId: actionHubPersonaId,
            origin: actionOrigin,
        },
    };
}

function updateStateOnLeaveGroup(
    state: NavigationState,
    payload: LeaveGroupActionPayload
): NavigationState {
    if (
        !state.currentQuery &&
        (!state.clickedHubPersona ||
            state.clickedHubPersona.origin !== 'DeepLink' ||
            !!state.clickedLeftNavItemId)
    ) {
        return updateState(
            state,
            payload.nextListItemId,
            state.clickedHubPersona ? state.clickedHubPersona.origin : 'personaList'
        );
    }

    return state;
}
